/* ============================================================
 * SSC Compare - overlay + floating-bar + cart-AJAX integration.
 *
 * Drives three pieces:
 *   1. The Compare checkboxes on knife-card-enabled product tiles
 *   2. The side-by-side comparison overlay (#ssc-compare-overlay)
 *   3. The persistent floating compare bar (#ssc-floating-compare)
 *
 * Selection state persists in sessionStorage so the bar survives
 * navigation between pages. Each entry carries enough metadata
 * (title, image, url, variantId, available) for the bar to render
 * thumbnails and fire cart-add even when card mounts aren't on
 * the current page.
 *
 * The compare overlay's slot population (cloning per-product cards
 * + diff highlighting) is unchanged; only the wiring around it grew.
 * ============================================================ */
(function () {
  'use strict';

  const MAX_SELECTED = 2;
  const STORAGE_KEY  = 'ssc_compare_selection';

  /* ── Selection state (persisted) ─────────────────────────────────── */
  /**
   * Array of { id, handle, title, url, image, variantId, available }
   * in selection order. Persisted in sessionStorage so navigation
   * doesn't lose the user's picks.
   */
  let selection = loadSelection();

  function loadSelection() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, MAX_SELECTED) : [];
    } catch (e) {
      return [];
    }
  }

  function saveSelection() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch (e) {
      /* quota or private-mode - silently swallow, state still works in-memory */
    }
  }

  function isSelected(productId) {
    return selection.some(s => s.id === productId);
  }

  function addToSelection(item) {
    if (selection.length >= MAX_SELECTED) return false;
    if (isSelected(item.id)) return false;
    selection.push(item);
    saveSelection();
    return true;
  }

  function removeFromSelection(productId) {
    const i = selection.findIndex(s => s.id === productId);
    if (i < 0) return false;
    selection.splice(i, 1);
    saveSelection();
    return true;
  }

  function clearSelection() {
    selection.length = 0;
    saveSelection();
  }

  /* ── Per-page DOM handles ────────────────────────────────────────── */
  const overlay      = document.getElementById('ssc-compare-overlay');
  const overlayBody  = overlay && overlay.querySelector('[data-ssc-compare-body]');
  const overlayNote  = overlay && overlay.querySelector('[data-ssc-compare-diff-note]');
  const artToggle    = overlay && overlay.querySelector('[data-ssc-compare-art-toggle]');
  const mountsRoot   = document.getElementById('ssc-card-mounts');
  const pageTrigger  = document.getElementById('compare-trigger'); // legacy, may be absent
  const floating     = document.getElementById('ssc-floating-compare');

  // Page focus before opening the modal - restored on close.
  let focusReturnTarget = null;

  // Active art mode inside the compare overlay (defaults to half).
  // Persisted in sessionStorage so reopening the modal restores choice.
  let compareArtMode = 'half';
  try {
    const saved = sessionStorage.getItem('ssc_compare_art_mode');
    if (saved === 'half' || saved === 'full') compareArtMode = saved;
  } catch (e) {}

  /* ── Checkbox reading: builds a selection entry from data-* attrs ─ */
  function readCheckboxData(input) {
    return {
      id:        input.dataset.productId        || '',
      handle:    input.dataset.productHandle    || '',
      title:     input.dataset.productTitle     || '',
      url:       input.dataset.productUrl       || '',
      image:     input.dataset.productImage     || '',
      variantId: input.dataset.productVariantId || '',
      available: input.dataset.productAvailable === 'true',
    };
  }

  /* ── Sync all UI elements to current selection state ─────────────── */
  function syncUI() {
    // 1. Checkboxes on this page - check the ones in selection, uncheck others
    document.querySelectorAll('[data-ssc-compare-input]').forEach(input => {
      const pid = input.dataset.productId;
      input.checked = isSelected(pid);
      // Disable unchecked checkboxes when cap is hit
      const wrap = input.closest('.ssc-compare-checkbox');
      if (selection.length >= MAX_SELECTED && !input.checked) {
        input.disabled = true;
        if (wrap) wrap.classList.add('is-disabled');
      } else {
        input.disabled = false;
        if (wrap) wrap.classList.remove('is-disabled');
      }
    });

    // 2. Legacy page-mounted trigger (if present)
    if (pageTrigger) {
      const count = pageTrigger.querySelector('[data-ssc-compare-count]');
      if (count) {
        count.textContent = selection.length === MAX_SELECTED
          ? '(ready)'
          : '(' + selection.length + '/' + MAX_SELECTED + ')';
      }
      pageTrigger.disabled = selection.length < MAX_SELECTED;
      pageTrigger.classList.toggle('is-ready', selection.length === MAX_SELECTED);
    }

    // 3. Floating bar (if present in the DOM)
    syncFloatingBar();
  }

  /* ── Floating compare bar rendering ──────────────────────────────── */
  function syncFloatingBar() {
    if (!floating) return;

    const n = selection.length;
    floating.dataset.count = String(n);
    // Auto-show only after first product selected (per brief - keeps
    // empty pages clean)
    if (n === 0) {
      floating.hidden = true;
      floating.dataset.open = 'false';
      return;
    }
    floating.hidden = false;
    floating.dataset.open = 'true';

    // Count text
    const countEl = floating.querySelector('[data-ssc-floating-count]');
    if (countEl) countEl.textContent = '(' + n + '/' + MAX_SELECTED + ')';

    // Slot thumbnails
    const slotEls = floating.querySelectorAll('[data-ssc-floating-slot]');
    slotEls.forEach((slot, idx) => {
      const entry = selection[idx];
      slot.innerHTML = '';
      if (!entry) {
        slot.classList.add('is-empty');
        const placeholder = document.createElement('span');
        placeholder.className = 'ssc-floating-compare-slot-placeholder';
        placeholder.textContent = idx === 0 ? 'Pick a knife' : 'Pick one more';
        slot.appendChild(placeholder);
        return;
      }
      slot.classList.remove('is-empty');

      const link = document.createElement('a');
      link.className = 'ssc-floating-compare-slot-link';
      link.href = entry.url || '#';
      link.setAttribute('aria-label', 'Open ' + entry.title);

      if (entry.image) {
        const img = document.createElement('img');
        img.className = 'ssc-floating-compare-slot-img';
        img.src = entry.image;
        img.alt = '';
        img.loading = 'lazy';
        link.appendChild(img);
      }

      const title = document.createElement('span');
      title.className = 'ssc-floating-compare-slot-title';
      title.textContent = entry.title;
      link.appendChild(title);

      slot.appendChild(link);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ssc-floating-compare-slot-remove';
      remove.setAttribute('aria-label', 'Remove ' + entry.title + ' from compare');
      remove.textContent = '✕';
      remove.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeFromSelection(entry.id);
        syncUI();
      });
      slot.appendChild(remove);
    });

    // Hint visibility - show only at 1/2
    const hint = floating.querySelector('[data-ssc-floating-hint]');
    if (hint) hint.hidden = n !== 1;

    // Actions visibility - show only at 2/2
    const actions = floating.querySelector('[data-ssc-floating-actions]');
    if (actions) actions.hidden = n !== MAX_SELECTED;

    // Add Both: disable if either product is unavailable
    const addBoth = floating.querySelector('[data-ssc-floating-addboth]');
    if (addBoth) {
      const bothAvail = selection.every(s => s.available && s.variantId);
      addBoth.disabled = !bothAvail;
      addBoth.title = bothAvail ? '' : 'One of the selected products is out of stock';
    }
  }

  /* ── Checkbox handlers ───────────────────────────────────────────── */
  function handleCheckboxChange(input) {
    const data = readCheckboxData(input);
    if (!data.id) return;
    if (input.checked) {
      if (selection.length >= MAX_SELECTED) {
        input.checked = false;
        return;
      }
      addToSelection(data);
    } else {
      removeFromSelection(data.id);
    }
    syncUI();
  }

  document.querySelectorAll('[data-ssc-compare-input]').forEach(input => {
    input.addEventListener('change', () => handleCheckboxChange(input));
  });

  /* ── Floating bar wiring ─────────────────────────────────────────── */
  if (floating) {
    const clearBtn = floating.querySelector('[data-ssc-floating-clear]');
    if (clearBtn) clearBtn.addEventListener('click', () => {
      clearSelection();
      syncUI();
    });

    const openBtn = floating.querySelector('[data-ssc-floating-open]');
    if (openBtn) openBtn.addEventListener('click', () => {
      openOverlay();
    });

    const addBothBtn = floating.querySelector('[data-ssc-floating-addboth]');
    if (addBothBtn) addBothBtn.addEventListener('click', () => addBothToCart(addBothBtn));
  }

  /* ── Compare overlay open / close ────────────────────────────────── */
  function openOverlay() {
    if (!overlay) return;
    if (selection.length !== MAX_SELECTED) return;
    if (!mountsRoot) {
      // We're not on the knives collection page - the card mounts
      // aren't here, so the overlay can't render. Send the user to
      // the knives page; the selection will still be in sessionStorage
      // and they can hit Compare from there.
      window.location.href = '/collections/knives';
      return;
    }
    syncArtToggleButtons();
    populateSlots();
    focusReturnTarget = document.activeElement;
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    const closeBtn = overlay.querySelector('.ssc-compare-close');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', handleOverlayKeyboard);
  }

  /* ── Art mode toggle inside the compare modal ────────────────────── */
  function syncArtToggleButtons() {
    if (!artToggle) return;
    artToggle.querySelectorAll('[data-ssc-compare-art-mode]').forEach(b => {
      const active = b.getAttribute('data-ssc-compare-art-mode') === compareArtMode;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }
  function setCompareArtMode(mode) {
    if (mode !== 'half' && mode !== 'full') return;
    compareArtMode = mode;
    try { sessionStorage.setItem('ssc_compare_art_mode', mode); } catch (e) {}
    syncArtToggleButtons();
    // Re-paint slots: flip data-art-mode on each cloned card AND
    // re-apply diff highlights (highlights live on the row class
    // which survives the mode flip, but a hard re-populate is
    // simpler and keeps everything consistent).
    populateSlots();
  }
  if (artToggle) {
    artToggle.querySelectorAll('[data-ssc-compare-art-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        setCompareArtMode(btn.getAttribute('data-ssc-compare-art-mode'));
      });
    });
  }

  function closeOverlay() {
    if (!overlay) return;
    overlay.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', handleOverlayKeyboard);
    if (focusReturnTarget && typeof focusReturnTarget.focus === 'function') {
      try { focusReturnTarget.focus(); } catch (e) { /* element may have gone */ }
    }
  }

  function handleOverlayKeyboard(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeOverlay();
      return;
    }
    if (e.key === 'Tab' && overlay) {
      const focusables = overlay.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  if (pageTrigger) pageTrigger.addEventListener('click', openOverlay);
  if (overlay) {
    overlay.querySelectorAll('[data-ssc-compare-dismiss]').forEach(el => {
      el.addEventListener('click', closeOverlay);
    });
    const ovClearBtn = overlay.querySelector('[data-ssc-compare-clear]');
    if (ovClearBtn) ovClearBtn.addEventListener('click', () => {
      clearSelection();
      syncUI();
      closeOverlay();
    });
  }

  /* ── Slot population (clone pre-rendered cards into the modal) ───── */
  function getMountFor(productId) {
    return mountsRoot && mountsRoot.querySelector('[data-ssc-card-mount][data-product-id="' + productId + '"]');
  }

  function readCardData(cardEl) {
    if (!cardEl) return null;
    const blob = cardEl.querySelector('[data-ssc-card-data]');
    if (!blob) return null;
    try { return JSON.parse(blob.textContent); }
    catch (e) { console.warn('[ssc-compare] failed to parse card data', e); return null; }
  }

  function populateSlots() {
    const slots = overlay.querySelectorAll('[data-ssc-compare-slot]');
    const cardData = [];

    slots.forEach((slot, idx) => {
      slot.innerHTML = '';
      const entry = selection[idx];
      if (!entry) return;
      const mount = getMountFor(entry.id);
      if (!mount) {
        slot.innerHTML = '<div class="ssc-compare-empty"><p>This product is no longer available.</p></div>';
        return;
      }
      const card = mount.querySelector('[data-ssc-card]');
      if (!card) return;
      const cloned = card.cloneNode(true);
      // Apply current compare art mode (user-toggleable via the
      // header tabs). Default is Half Art for spec readability.
      cloned.setAttribute('data-art-mode', compareArtMode);
      slot.appendChild(cloned);
      cardData.push(readCardData(cloned));

      // Feature 1: make hero img + title in the cloned card clickable to the product page
      const heroImg = cloned.querySelector('.ssc-card-hero-img');
      if (heroImg && entry.url) {
        const link = document.createElement('a');
        link.href = entry.url;
        link.setAttribute('aria-label', 'View ' + entry.title);
        link.style.display = 'block';
        link.style.position = 'absolute';
        link.style.inset = '0';
        link.style.zIndex = '0';
        const heroBox = cloned.querySelector('.ssc-card-hero');
        if (heroBox) heroBox.appendChild(link);
      }
      const titleEl = cloned.querySelector('.ssc-card-title');
      if (titleEl && entry.url) {
        const wrap = document.createElement('a');
        wrap.href = entry.url;
        wrap.style.color = 'inherit';
        wrap.style.textDecoration = 'none';
        wrap.innerHTML = titleEl.innerHTML;
        titleEl.innerHTML = '';
        titleEl.appendChild(wrap);
      }

      // Feature 1: append an Add to Cart button + a small "View full" link
      const ctas = document.createElement('div');
      ctas.className = 'ssc-compare-card-ctas';

      const cartBtn = document.createElement('button');
      cartBtn.type = 'button';
      cartBtn.className = 'ssc-compare-cart-btn';
      if (!entry.available || !entry.variantId) {
        cartBtn.textContent = 'Out of stock';
        cartBtn.disabled = true;
        cartBtn.classList.add('is-disabled');
      } else {
        cartBtn.textContent = 'Add to Cart';
        cartBtn.dataset.variantId = entry.variantId;
        cartBtn.addEventListener('click', () => addOneToCart(entry, cartBtn));
      }
      ctas.appendChild(cartBtn);

      const viewLink = document.createElement('a');
      viewLink.className = 'ssc-compare-view-link';
      viewLink.href = entry.url;
      viewLink.textContent = 'View full product';
      ctas.appendChild(viewLink);

      cloned.appendChild(ctas);
    });

    applyDiffHighlights(cardData);

    // Feature 1: header-level Add Both button (only when 2 selected)
    const header = overlay.querySelector('.ssc-compare-header');
    if (header) {
      const existing = header.querySelector('[data-ssc-overlay-addboth]');
      if (existing) existing.remove();
      if (selection.length === MAX_SELECTED) {
        const bothAvail = selection.every(s => s.available && s.variantId);
        const addBoth = document.createElement('button');
        addBoth.type = 'button';
        addBoth.className = 'ssc-compare-addboth';
        addBoth.dataset.sscOverlayAddboth = '';
        addBoth.textContent = bothAvail ? 'Add Both to Cart' : 'One is out of stock';
        addBoth.disabled = !bothAvail;
        if (bothAvail) addBoth.addEventListener('click', () => addBothToCart(addBoth));
        // Insert before the close button
        const closeBtn = header.querySelector('.ssc-compare-close');
        if (closeBtn) header.insertBefore(addBoth, closeBtn);
        else header.appendChild(addBoth);
      }
    }
  }

  /* ── Diff highlighting (unchanged from prior version) ────────────── */
  function applyDiffHighlights(cardData) {
    if (!overlayNote) return;
    if (cardData.length !== 2 || !cardData[0] || !cardData[1]) {
      overlayNote.textContent = '';
      return;
    }
    const [a, b] = cardData;
    if (a.category !== b.category) {
      overlayNote.textContent = 'Different categories - diff highlights only shown when both cards match.';
      return;
    }
    const slotEls = overlay.querySelectorAll('[data-ssc-compare-slot]');
    const cardA = slotEls[0].querySelector('[data-ssc-card]');
    const cardB = slotEls[1].querySelector('[data-ssc-card]');
    const rowsA = cardA ? cardA.querySelectorAll('.ssc-card-spec-row') : [];
    const rowsB = cardB ? cardB.querySelectorAll('.ssc-card-spec-row') : [];

    let diffCount = 0;
    const limit = Math.min(rowsA.length, rowsB.length);
    for (let i = 0; i < limit; i++) {
      const labelA = rowsA[i].querySelector('dt');
      const labelB = rowsB[i].querySelector('dt');
      const valueA = rowsA[i].querySelector('dd');
      const valueB = rowsB[i].querySelector('dd');
      if (!labelA || !labelB || !valueA || !valueB) continue;
      if (labelA.textContent.trim() !== labelB.textContent.trim()) continue;
      const va = valueA.textContent.trim();
      const vb = valueB.textContent.trim();
      if (va === vb) { rowsA[i].classList.add('is-same'); rowsB[i].classList.add('is-same'); }
      else { rowsA[i].classList.add('is-diff'); rowsB[i].classList.add('is-diff'); diffCount++; }
    }
    if (diffCount === 0) overlayNote.textContent = 'Identical specs across the board.';
    else overlayNote.textContent = diffCount + ' spec' + (diffCount === 1 ? '' : 's') + ' differ.';
  }

  /* ── Cart AJAX (one item / both items) ───────────────────────────── */
  async function postCartAdd(items, btn) {
    const orig = btn ? btn.textContent : '';
    if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.description || ('HTTP ' + res.status));
      }
      // Refresh cart badge - reuse the same approach as global theme.liquid addToCart
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const badge = document.querySelector('.nav-cart-count');
        if (badge) badge.textContent = cart.item_count;
        else if (cart.item_count > 0) {
          const cartEl = document.querySelector('.nav-cart');
          if (cartEl) {
            const b = document.createElement('div');
            b.className = 'nav-cart-count';
            b.textContent = cart.item_count;
            cartEl.appendChild(b);
          }
        }
      } catch (e) { /* badge refresh failure is non-fatal */ }

      if (btn) { btn.textContent = '✓ Added'; }
      setTimeout(() => { if (btn) { btn.textContent = orig; btn.disabled = false; } }, 1800);
      return true;
    } catch (e) {
      console.error('[ssc-compare] cart add failed', e);
      if (btn) { btn.textContent = 'Error - try again'; }
      setTimeout(() => { if (btn) { btn.textContent = orig; btn.disabled = false; } }, 2200);
      return false;
    }
  }

  function addOneToCart(entry, btn) {
    if (!entry.variantId) return;
    postCartAdd([{ id: parseInt(entry.variantId, 10), quantity: 1 }], btn);
  }

  function addBothToCart(btn) {
    const items = selection
      .filter(s => s.available && s.variantId)
      .map(s => ({ id: parseInt(s.variantId, 10), quantity: 1 }));
    if (items.length === 0) return;
    postCartAdd(items, btn);
  }

  /* ── Init: sync UI from persisted selection ──────────────────────── */
  syncUI();
})();
